import isEmpty from 'lodash/isEmpty'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import Form from 'main/components/common/Form'
import { USER_QUERY } from 'main/gql/user'
import { Button, colors, spacing, Text } from 'main/ui'
import { formatDateInput } from 'main/util/date'
import ChildSection, { IParams as IChildParams } from './ChildSection'
import {
  SUBMIT_INTERMEDIATE_PARENT_SIGN_UP_FORM_MUTATION,
  SubmitIntermediateParentSignUpForm,
  SubmitIntermediateParentSignUpFormVariables,
} from './gql'
import PasswordSection from './PasswordSection'
import { IParams, IValidation } from './types'
import { initialParams, validate } from './util'

interface IProps {
  cta: string
  onComplete: () => void
}

export default function FullForm(props: IProps) {
  const { cta, onComplete } = props

  const [params, setParams] = React.useState<IParams>(initialParams())
  const [validation, setValidation] = React.useState<IValidation>({})
  const [error, setError] = React.useState<boolean>(false)

  return (
    <Mutation<SubmitIntermediateParentSignUpForm, SubmitIntermediateParentSignUpFormVariables>
      mutation={SUBMIT_INTERMEDIATE_PARENT_SIGN_UP_FORM_MUTATION}
      onError={() => setError(true)}
      refetchQueries={[{ query: USER_QUERY }]}
    >
      {(mutate, { loading }) => (
        <Form onSubmit={() => handleSubmit(params, setValidation, setError, mutate, onComplete)}>
          <div css={{ marginBottom: spacing.majorScale(4) }}>
            <PasswordSection
              params={{ password: params.password }}
              validation={{ password: validation.password }}
              onChange={changes => handleParamsChange(changes, setParams)}
            />
          </div>

          <div css={{ marginBottom: spacing.majorScale(error ? 2 : 4) }}>
            <ChildSection
              params={params.child}
              validation={validation.child || {}}
              onChange={changes => handleChildParamsChange(changes, setParams)}
            />
          </div>

          {error && (
            <div css={{ marginBottom: spacing.majorScale(2) }}>
              <Text css={{ color: colors.text.error }}>
                Something went wrong! Please try again.
              </Text>
            </div>
          )}

          <Button kind="regular" disabled={loading} full>
            {loading ? 'Submitting...' : cta}
          </Button>
        </Form>
      )}
    </Mutation>
  )
}

function handleParamsChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleChildParamsChange(
  changes: Partial<IChildParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({
    ...params,
    child: { ...params.child, ...changes },
  }))
}

function handleSubmit(
  params: IParams,
  setValidation: (validation: IValidation) => void,
  setError: (error: boolean) => void,
  mutate: MutationFunction<
    SubmitIntermediateParentSignUpForm,
    SubmitIntermediateParentSignUpFormVariables
  >,
  onComplete: () => void,
) {
  const validation = validate(params)
  setError(false)
  setValidation(validation)
  if (!isEmpty(validation)) {
    return
  }

  mutate({
    variables: {
      child: {
        birthday: formatDateInput(params.child.birthday),
        desiredEnrollmentDate: formatDateInput(params.child.desiredEnrollmentDate),
      },
      user: {
        password: params.password.trim(),
      },
    },
  }).then(() => onComplete())
}
